import { useEffect, useRef } from 'react';

/**
 * Fades `[data-reveal]` blocks in as they scroll into view (design template
 * §reveal-on-scroll).
 *
 * The hiding class is added by JS and never by the stylesheet, which is the
 * whole trick: with no script, no `IntersectionObserver`, or a visitor who has
 * asked for reduced motion, nothing is ever hidden in the first place, so the
 * page cannot end up blank waiting for an animation that will not run. That
 * matters more here than anywhere else in the app — this is the page a stranger
 * sees first, and there is no shell around it to prove the app loaded.
 */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const targets = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    for (const el of targets) el.classList.add('lp-reveal');

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('lp-reveal--in');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
    for (const el of targets) observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return ref;
}
